import React, { useEffect, useState } from 'react';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import { Modal, Panel } from 'react-bootstrap';
import ReactEcharts from 'echarts-for-react';

import { fetchApi } from '../../Network/NetworkService';
import { notif } from '../Ui/Notification';
import toYMD from '../../Services/toYMD';

const fmtEuro = (n) => `${Math.round(n).toLocaleString('fr-FR')} €`;
const safeDiv = (a, b) => (b > 0 ? a / b : 0);

const kpiStyle = (color) => ({
    flex: '1 1 160px',
    minWidth: 160,
    padding: 16,
    borderRadius: 8,
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderLeft: `4px solid ${color}`,
});

const GoogleAdsPerformance = () => {
    const oneMonthBefore = new Date();
    oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1);

    const [dateValue, setDateValue] = useState([
        oneMonthBefore.toISOString().split('T')[0],
        new Date().toISOString().split('T')[0],
    ]);
    const [loading, setLoading] = useState(false);
    const [timeseries, setTimeseries] = useState([]);
    const [dbDaily, setDbDaily] = useState([]);
    const [keywords, setKeywords] = useState([]);

    const fetchAll = () => {
        setLoading(true);
        setTimeseries([]);
        setDbDaily([]);
        setKeywords([]);

        const start = `${toYMD(dateValue[0])}T00:00:00.000Z`;
        const end = `${toYMD(dateValue[1])}T23:59:59.999Z`;
        const payload = { dateValue: [toYMD(dateValue[0]), toYMD(dateValue[1])] };

        const calls = [
            fetchApi('/analyticData/getGoogleAdsTimeseries', { method: 'POST', body: JSON.stringify(payload) }).catch(() => ({ data: { days: [] } })),
            fetchApi('/analyticData/getGoogleAdsKeywords', { method: 'POST', body: JSON.stringify(payload) }).catch(() => ({ data: { keywords: [] } })),
            fetchApi(`/requests/analytics/daily-by-source?start=${start}&end=${end}&source=google`).catch(() => ({ data: { days: [] } })),
        ];

        Promise.all(calls)
            .then(([ts, kw, db]) => {
                setTimeseries((ts.data && ts.data.days) || []);
                setKeywords((kw.data && kw.data.keywords) || []);
                setDbDaily((db.data && db.data.days) || []);
                setLoading(false);
            })
            .catch(err => {
                setLoading(false);
                notif.addNotification('Erreur', 'Erreur de récupération des données: ' + err, 'warning');
            });
    };

    useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [dateValue]);

    // Join GA4 daily cost with DB daily paid count + revenue on date.
    // One-sided join driven by GA4: days with paid clients but no GA4 spend row
    // (e.g. campaign paused for that day) are dropped. Totals below reflect only
    // days with Google Ads activity — not absolute google-source revenue.
    const joined = timeseries.map(d => {
        const db = dbDaily.find(x => x.date === d.date) || { count: 0, revenue: 0 };
        return {
            date: d.date,
            cost: d.cost,
            clicks: d.clicks,
            sessions: d.sessions,
            paidCount: db.count,
            paidRevenue: db.revenue,
            cpa: safeDiv(d.cost, db.count),
            roas: safeDiv(db.revenue, d.cost),
        };
    });

    const totals = joined.reduce((acc, d) => ({
        cost: acc.cost + d.cost,
        clicks: acc.clicks + d.clicks,
        sessions: acc.sessions + d.sessions,
        paidCount: acc.paidCount + d.paidCount,
        paidRevenue: acc.paidRevenue + d.paidRevenue,
    }), { cost: 0, clicks: 0, sessions: 0, paidCount: 0, paidRevenue: 0 });

    const totalCPA = safeDiv(totals.cost, totals.paidCount);
    const totalROAS = safeDiv(totals.paidRevenue, totals.cost);

    const dates = joined.map(d => d.date);

    const spendChart = {
        title: { text: 'Dépense quotidienne', left: 'center', textStyle: { fontSize: 14 } },
        tooltip: { trigger: 'axis', valueFormatter: v => `${v} €` },
        grid: { left: 60, right: 20, top: 40, bottom: 40 },
        xAxis: { type: 'category', data: dates, axisLabel: { rotate: 45 } },
        yAxis: { type: 'value', axisLabel: { formatter: '{value} €' } },
        series: [{ name: 'Dépense', type: 'line', smooth: true, data: joined.map(d => d.cost), itemStyle: { color: '#1976d2' }, areaStyle: {} }]
    };

    const cpaChart = {
        title: { text: 'CPA quotidien (coût / client payant)', left: 'center', textStyle: { fontSize: 14 } },
        tooltip: { trigger: 'axis', valueFormatter: v => `${v.toFixed ? v.toFixed(2) : v} €` },
        grid: { left: 60, right: 20, top: 40, bottom: 40 },
        xAxis: { type: 'category', data: dates, axisLabel: { rotate: 45 } },
        yAxis: { type: 'value', axisLabel: { formatter: '{value} €' } },
        series: [{ name: 'CPA', type: 'line', smooth: true, data: joined.map(d => Number(d.cpa.toFixed(2))), itemStyle: { color: '#ff9800' } }]
    };

    const roasChart = {
        title: { text: 'ROAS quotidien (CA / dépense)', left: 'center', textStyle: { fontSize: 14 } },
        tooltip: { trigger: 'axis', valueFormatter: v => `${v.toFixed ? v.toFixed(2) : v}x` },
        grid: { left: 60, right: 20, top: 40, bottom: 40 },
        xAxis: { type: 'category', data: dates, axisLabel: { rotate: 45 } },
        yAxis: { type: 'value', axisLabel: { formatter: '{value}x' } },
        series: [{ name: 'ROAS', type: 'line', smooth: true, data: joined.map(d => Number(d.roas.toFixed(2))), itemStyle: { color: '#4caf50' },
            markLine: { silent: true, symbol: 'none', data: [{ yAxis: 1, label: { formatter: 'Seuil 1x' }, lineStyle: { color: '#999', type: 'dashed' } }] }
        }]
    };

    return (
        <Panel>
            <Modal show={loading} backdrop="static" keyboard={false} size="lg" className="modal-spinner">
                <span className="loader" />
                {loading && <p>Récupération des données en cours...</p>}
            </Modal>

            <Panel.Heading>
                <h6>Performance Google Ads</h6>
            </Panel.Heading>

            <Panel.Body>
                <div style={{ marginBottom: 20 }}>
                    <DateRangePicker
                        locale="fr-FR"
                        onChange={value => setDateValue(value)}
                        value={dateValue}
                        maxDate={new Date()}
                        clearIcon={null}
                    />
                </div>

                {/* Summary KPIs */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                    <div style={kpiStyle('#1976d2')}>
                        <div style={{ color: '#666', fontSize: 12 }}>Dépense totale</div>
                        <div style={{ fontSize: 24, fontWeight: 'bold' }}>{fmtEuro(totals.cost)}</div>
                    </div>
                    <div style={kpiStyle('#1976d2')}>
                        <div style={{ color: '#666', fontSize: 12 }}>Clics</div>
                        <div style={{ fontSize: 24, fontWeight: 'bold' }}>{totals.clicks.toLocaleString('fr-FR')}</div>
                    </div>
                    <div style={kpiStyle('#1976d2')}>
                        <div style={{ color: '#666', fontSize: 12 }}>Sessions</div>
                        <div style={{ fontSize: 24, fontWeight: 'bold' }}>{totals.sessions.toLocaleString('fr-FR')}</div>
                    </div>
                    <div style={kpiStyle('#4caf50')}>
                        <div style={{ color: '#666', fontSize: 12 }}>Clients payants</div>
                        <div style={{ fontSize: 24, fontWeight: 'bold' }}>{totals.paidCount.toLocaleString('fr-FR')}</div>
                    </div>
                    <div style={kpiStyle('#4caf50')}>
                        <div style={{ color: '#666', fontSize: 12 }}>CA</div>
                        <div style={{ fontSize: 24, fontWeight: 'bold' }}>{fmtEuro(totals.paidRevenue)}</div>
                    </div>
                    {/* Seuils: CPA vert si <50€ (prix consultation ~30-45€), ROAS vert si >1x */}
                    <div style={kpiStyle(totalCPA > 0 && totalCPA < 50 ? '#4caf50' : '#f44336')}>
                        <div style={{ color: '#666', fontSize: 12 }}>CPA moyen</div>
                        <div style={{ fontSize: 24, fontWeight: 'bold' }}>{totalCPA.toFixed(2)} €</div>
                    </div>
                    <div style={kpiStyle(totalROAS >= 1 ? '#4caf50' : '#f44336')}>
                        <div style={{ color: '#666', fontSize: 12 }}>ROAS moyen</div>
                        <div style={{ fontSize: 24, fontWeight: 'bold' }}>{totalROAS.toFixed(2)}x</div>
                    </div>
                </div>

                {/* Time-series charts */}
                {joined.length > 0 && (
                    <div>
                        <div style={{ marginBottom: 30 }}>
                            <ReactEcharts option={spendChart} style={{ height: 300 }} notMerge={true} />
                        </div>
                        <div style={{ marginBottom: 30 }}>
                            <ReactEcharts option={cpaChart} style={{ height: 300 }} notMerge={true} />
                        </div>
                        <div style={{ marginBottom: 30 }}>
                            <ReactEcharts option={roasChart} style={{ height: 300 }} notMerge={true} />
                        </div>
                    </div>
                )}

                {/* Keyword table */}
                {keywords.length > 0 && (
                    <div style={{ marginTop: 30 }}>
                        <h5 style={{ marginBottom: 10 }}>Mots-clés (top par dépense)</h5>
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table table-hover" style={{ fontSize: 13 }}>
                                <thead>
                                    <tr>
                                        <th>Mot-clé</th>
                                        <th>Campagne</th>
                                        <th style={{ textAlign: 'right' }}>Impressions</th>
                                        <th style={{ textAlign: 'right' }}>Clics</th>
                                        <th style={{ textAlign: 'right' }}>Sessions</th>
                                        <th style={{ textAlign: 'right' }}>Dépense</th>
                                        <th style={{ textAlign: 'right' }}>CTR</th>
                                        <th style={{ textAlign: 'right' }}>CPC</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {keywords.map(k => {
                                        const ctr = safeDiv(k.clicks, k.impressions) * 100;
                                        const cpc = safeDiv(k.cost, k.clicks);
                                        return (
                                            <tr key={`${k.keyword}|${k.campaign}`}>
                                                <td>{k.keyword}</td>
                                                <td>{k.campaign}</td>
                                                <td style={{ textAlign: 'right' }}>{k.impressions.toLocaleString('fr-FR')}</td>
                                                <td style={{ textAlign: 'right' }}>{k.clicks.toLocaleString('fr-FR')}</td>
                                                <td style={{ textAlign: 'right' }}>{k.sessions.toLocaleString('fr-FR')}</td>
                                                <td style={{ textAlign: 'right' }}>{fmtEuro(k.cost)}</td>
                                                <td style={{ textAlign: 'right' }}>{ctr.toFixed(2)}%</td>
                                                <td style={{ textAlign: 'right' }}>{cpc.toFixed(2)} €</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <p style={{ color: '#888', fontSize: 12, marginTop: 8 }}>
                            * Clients payants et CA par mot-clé non disponibles tant que utm_campaign/utm_term ne sont pas tracés sur les requests. CPA et ROAS sont calculés au niveau agrégé.
                        </p>
                    </div>
                )}
            </Panel.Body>
        </Panel>
    );
};

export default GoogleAdsPerformance;
